import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BASE_URLS } from "../../components/env";
import { getSupabase } from "../../components/supabaseClient";
import MedsHistory, {
  dayKey,
  todayKey,
  prettyTime,
} from "../patient/MedsHistory";
// Calendar + change-timeline styles live in the patient portal CSS. They
// are scoped to the doctor modal too, so pull the sheet in even when the
// user has never opened the patient /meds page.
import "../css/PatientPortal.css";
import {
  loadMedsCacheSync,
  readMedsCache,
  writeMedsCache,
  readAutoScanStampToday,
  writeAutoScanStampToday,
  localDateKey,
} from "../../utils/medsCache";

async function getAccessTokenSafe() {
  try {
    const supabase = await getSupabase();
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    return data && data.session ? data.session.access_token : null;
  } catch (err) {
    console.warn("ClinicPatientMedsModal: getAccessToken failed", err);
    return null;
  }
}

function backendBase() {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return BASE_URLS.local.backend;
  return (
    (process.env.REACT_APP_BACKEND_URL &&
      process.env.REACT_APP_BACKEND_URL.trim() !== "" &&
      process.env.REACT_APP_BACKEND_URL) ||
    BASE_URLS.render.backend
  );
}

// Render a YYYY-MM-DD date (the format the backend echoes back) into a
// clinician-friendly short form ("Jan 4, 2026"). Anything unparseable falls
// back to the raw string so the source info isn't silently dropped.
function prettyVisitDate(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(String(iso).trim());
  if (!m) return String(iso);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[parseInt(m[2], 10) - 1] || m[2];
  return `${month} ${parseInt(m[3], 10)}, ${m[1]}`;
}

// Loose key used to spot the same medicine written two different ways
// ("Lisinopril 20mg tablet" vs "lisinopril 20 mg") across scans / list
// reloads. The patient-side `PatientMeds.js` uses the same key, so the
// doctor's modal and the patient's page agree on what "the same drug"
// means. Digit↔letter boundaries get a separator so the common
// "20mg" / "20 mg" shorthand-vs-spelled-out variants collapse together.
function normalizeMedName(name) {
  const s = String(name || "").toLowerCase();
  return s
    .replace(/([a-z])([0-9])/g, "$1 $2")
    .replace(/([0-9])([a-z])/g, "$1 $2")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

// Auto-scan dedup. The chart-header "💊 Meds" button now opens the modal
// AND fires the "Add meds from visits" backend flow on the user's behalf
// (so they don't have to click twice). That flow is expensive (reads
// every visit doc, runs Claude) and idempotent — re-running it within
// the same day just returns "N were already on the list", which is noise.
// The dedup stamp + cache helpers now live in `utils/medsCache.js` and
// use localStorage (not sessionStorage), so a hard refresh, a new tab,
// or coming back to the chart after lunch all skip the scan and reuse
// the cached list. The in-modal manual re-scan button still re-runs the
// scan on demand because it always replaces the stored timestamp.

async function clinicMedsApi(patientId, path, method, body) {
  const token = await getAccessTokenSafe();
  const requestId = `cmeds-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const headers = { "Content-Type": "application/json", "X-Request-ID": requestId };
  if (token) headers.Authorization = `Bearer ${token}`;
  const query = path.includes("?") ? `&patient_id=${encodeURIComponent(patientId)}` : `?patient_id=${encodeURIComponent(patientId)}`;
  const url = `${backendBase()}/api/clinic/patient-meds${path}${query}`;
  console.info("ClinicPatientMeds request", { requestId, method, path });
  const res = await fetch(url, {
    method,
    headers,
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await res.json().catch(() => ({}));
  console.info("ClinicPatientMeds response", { requestId, method, path, status: res.status });
  if (!res.ok) {
    const err = new Error(
      (payload && (payload.error || payload.detail)) || `Request failed (${res.status}).`
    );
    err.status = res.status;
    err.payload = payload;
    err.requestId = requestId;
    throw err;
  }
  return payload;
}

function LoadingClock({ label = "Loading…" }) {
  return (
    <span
      role="status"
      aria-live="polite"
      className="inline-flex items-center gap-1.5"
    >
      <span className="relative inline-flex h-3.5 w-3.5 items-center justify-center">
        <svg
          className="absolute inset-0 h-3.5 w-3.5 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.35" strokeWidth="3" />
          <path
            d="M21 12a9 9 0 0 0-9-9"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
        <svg
          className="h-3.5 w-3.5"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="6.25" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M12 7.5V12l2.5 1.6"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span>{label}</span>
    </span>
  );
}

// Mirror the patient-side `mapMed` so the doctor modal and the patient
// /meds page render the same calendar from the same data. Anything the
// patient page needs (`startedAt`, `ended_at` → `endedAt`, `dosesLog`,
// `takenAt`, etc.) is derived from the same API payload. Editor fields
// (`added_by_*` / `discontinued_by_*`) are surfaced so the history view
// can show "Added by Dr. Jane Lee" / "Stopped by patient".
function mapCurrent(m) {
  const log = Array.isArray(m.doses_log) ? m.doses_log : [];
  // Recover today's dose time from the log so "Taken at 8:04 AM" survives a
  // page reload, same as on the patient page.
  const todaysDose = log.find(
    (d) => d && dayKey(d.date) === todayKey() && d.taken !== false,
  );
  return {
    id: m.id,
    name: m.name,
    dose: m.dose || "",
    reason: m.reason || "",
    slot:
      m.frequency === "evening" || m.schedule === "evening"
        ? "evening"
        : "morning",
    taken: Boolean(m.taken_today),
    takenAt: todaysDose ? prettyTime(todaysDose.taken_at) : undefined,
    dosesLog: log,
    startedAt: m.started_at || null,
    endedAt: m.ended_at || null,
    prescriber: m.prescriber || "",
    notes: m.notes || "",
    addedBy: pickEditor(m, "added_by"),
    discontinuedBy: pickEditor(m, "discontinued_by"),
  };
}

function mapHistory(m) {
  return {
    ...mapCurrent(m),
    endedAt: m.ended_at || null,
    removedAt: m.ended_at || null,
  };
}

function pickEditor(med, prefix) {
  if (!med || typeof med !== "object") return null;
  const userId = med[`${prefix}_user_id`];
  const name = med[`${prefix}_name`];
  const email = med[`${prefix}_email`];
  const role = med[`${prefix}_role`];
  const kind = med[`${prefix}_kind`];
  const at = med[`${prefix}_at`];
  if (!userId && !name && !email && !kind) return null;
  return {
    userId: userId || null,
    name: name || null,
    email: email || null,
    role: role || null,
    kind: kind || null,
    at: at || null,
  };
}

export default function ClinicPatientMedsModal({ patient, onClose, currentVisitOverride = null, autoScan = false }) {
  const patientId = patient?.id;
  // Synchronous cache hydration — paint the cached list on the first
  // render so opening this modal from the chart header feels instant.
  // The cache is the same `medsCache.js` one the patient page writes;
  // both surfaces share the same shape so no remapping is needed.
  const cacheSeed = loadMedsCacheSync();
  const [meds, setMeds] = useState(() =>
    cacheSeed && Array.isArray(cacheSeed.current) ? cacheSeed.current : []
  );
  const [history, setHistory] = useState(() =>
    cacheSeed && Array.isArray(cacheSeed.history) ? cacheSeed.history : []
  );
  const [loading, setLoading] = useState(!cacheSeed);
  const [loadError, setLoadError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [docScanning, setDocScanning] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  // Flips true once the mount effect has run. Gates the auto-scan so it
  // can't fire before we know whether the cache / first server response
  // already populated the list.
  const [bootstrapped, setBootstrapped] = useState(false);
  // One-shot guard for `autoScan` — the meds button in the chart header
  // ("💊 Meds") now opens this modal AND fires the same backend flow as
  // clicking "Add meds from visits" inside it, so doctors don't have to
  // click twice. The ref fires the scan exactly once per mount; the user
  // can still press the in-modal button later to re-scan. Reset on each
  // mount (fresh component each time the modal opens).
  const autoScanFiredRef = useRef(false);
  // Visible during the auto-scan from the chart header so we don't flash a
  // stale meds table before the latest scan has refreshed the list. Starts
  // `true` when `autoScan` is requested AND we don't already have a
  // successful auto-scan recorded for today (see `readAutoScanStampToday`)
  // — repeat opens inside the same local day skip the scan entirely and
  // the table paints from the cache + silent `refreshMeds` like usual.
  // The user-initiated re-scan (in-modal button) is handled by the body
  // overlay instead of replacing the whole table. useMemo so the IIFE
  // below doesn't re-read localStorage on every render.
  const initialAutoScan = useMemo(() => {
    if (autoScan !== true) return false;
    const stamp = readAutoScanStampToday();
    return !stamp || stamp.day !== localDateKey();
  }, [autoScan]);
  const [scanInFlight, setScanInFlight] = useState(initialAutoScan);
  // Per-medication source-visit dates returned by the LLM on the last
  // "Add meds from visits" scan, keyed by normalized med name so they
  // survive the post-scan `refreshMeds()` rebuild. We keep them around
  // until the next scan (or the patient list refreshes from outside the
  // modal — a nav away unmounts the component anyway) so the clinician
  // can SEE which visits each med was extracted from. Empty `{}` until
  // the first scan completes; only entries with at least one date are
  // rendered (see `MedRow`'s `sourceVisitDates` prop).
  const [medSourceDates, setMedSourceDates] = useState({});
  const [draft, setDraft] = useState({ name: "", dose: "", reason: "", slot: "morning" });

  const refreshMeds = useCallback(async ({ silent = false } = {}) => {
    if (!patientId) return false;
    const hadCache = Boolean(readMedsCache());
    if (!silent && !hadCache) setLoadError(null);
    if (!silent) setLoading(true);
    try {
      const data = await clinicMedsApi(patientId, "/", "GET");
      const shape = (data && data.meds) || {};
      const current = Array.isArray(shape.current) ? shape.current : [];
      const past = Array.isArray(shape.history) ? shape.history : [];
      // Map to the shape <MedsHistory> needs: id, name, dose, reason, slot,
      // taken, takenAt, dosesLog, startedAt, endedAt, prescriber, notes.
      // The shared patient mapper already returns that exact shape from the
      // same API payload — keep the inline mapping here in sync so the
      // doctor and the patient see the same calendar from the same data.
      const mappedCurrent = current.map(mapCurrent);
      const mappedHistory = past.map(mapHistory);
      setMeds(mappedCurrent);
      setHistory(mappedHistory);
      // Persist so the next modal open paints from cache instantly.
      writeMedsCache({ current: mappedCurrent, history: mappedHistory });
      return true;
    } catch (err) {
      // `_resolve_clinic_patient` returns "patient_not_found" when the
      // chart uuid isn't in any notes-c table the doctor can read AND the
      // stub-row seed fell through. The doctor's meds edits can still
      // land via the POST handler (which retries resolve + insert on its
      // own), so we don't lock the modal — we just paint an empty list and
      // surface a soft info flash instead of a hard red error banner.
      const isMissing = err && err.payload && err.payload.error === "patient_not_found";
      if (isMissing) {
        setMeds([]);
        setHistory([]);
        setLoadError(null);
        setFlash({
          type: "info",
          text: "This patient isn't linked to the portal yet — the medication list is empty until you add one or the patient enrolls.",
        });
      } else {
        if (!silent) {
          setLoadError(err.message || "Failed to load medicines");
          setMeds([]);
          setHistory([]);
        }
      }
      return false;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Re-hydrate from cache in case another tab wrote between the sync
      // seed read in useState and this effect running.
      const cached = readMedsCache();
      if (cached && cached.meds) {
        if (!cancelled) {
          setMeds(
            Array.isArray(cached.meds.current) ? cached.meds.current : []
          );
          setHistory(
            Array.isArray(cached.meds.history) ? cached.meds.history : []
          );
        }
      }
      // Reconcile silently so the modal never flashes back to a
      // skeleton once the cache (or a previous open) has filled the
      // table. Only a genuine first-time-open-with-no-cache path ever
      // sees the loading state — and even then, just briefly.
      const ok = await refreshMeds({ silent: true });
      if (!cancelled) {
        if (!ok && !cached) {
          setLoadError("Couldn't load this patient's medicines.");
        }
        setLoading(false);
        setBootstrapped(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshMeds]);

  // When the doctor clicks the 💊 Meds header button in the chart, open the
  // modal and immediately fire "Add meds from visits" against whatever the
  // editor currently shows — but only if we haven't already auto-scanned
  // successfully for this patient today (see `readAutoScanStampToday` in
  // `medsCache.js`). `handleScanLatestDocument` only reads
  // `currentVisitOverride` at the time of the call, so the small delay
  // just gives React's commit phase a tick to attach the modal — the
  // Quill instance is a singleton the chart editor already mounted, and
  // is what `currentVisitOverride.getQuill` returns. The dedup in the
  // backend means re-clicking "Add meds from visits" on a patient whose
  // meds are already up-to-date just shows "N were already on the list"
  // with no duplicates.
  useEffect(() => {
    if (!autoScan) return;
    if (!bootstrapped) return;
    if (!initialAutoScan) return;
    if (autoScanFiredRef.current) return;
    autoScanFiredRef.current = true;
    const handle = setTimeout(() => {
      if (!docScanning) {
        handleScanLatestDocument().catch(() => {});
      }
    }, 250);
    return () => clearTimeout(handle);
    // We deliberately depend on `autoScan` only — running again on a
    // different value mid-mount is fine, but re-running on every render
    // would re-fire the scan and confuse the clinician. `handleScanLatestDocument`
    // is stable enough (closes over `currentVisitOverride`, `patientId`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScan, bootstrapped]);

  const toggleTaken = async (id) => {
    const med = meds.find((m) => m.id === id);
    if (!med) return;
    const undo = Boolean(med.taken);
    const prevLog = Array.isArray(med.dosesLog) ? med.dosesLog : [];
    const nowIso = new Date().toISOString();
    const today = todayKey();
    // Mirror what the server does to `doses_log` so the calendar reflects
    // the tick right away (same trick the patient page uses). Without this
    // the tick would disappear from today's cell until the next refresh.
    const nextLog = undo
      ? prevLog.filter((d) => !(d && dayKey(d.date) === today))
      : [...prevLog, { date: today, taken_at: nowIso, taken: true }];
    setMeds((cur) =>
      cur.map((m) =>
        m.id === id
          ? {
              ...m,
              taken: !undo,
              takenAt: !undo ? prettyTime(nowIso) : undefined,
              dosesLog: nextLog,
            }
          : m
      )
    );
    try {
      await clinicMedsApi(patientId, `/${encodeURIComponent(id)}/taken/`, "POST", { patient_id: patientId, undo });
      // Reconcile with the server (taken_today is the source of truth).
      await refreshMeds({ silent: true });
    } catch (err) {
      setFlash({ type: "error", text: `Couldn't save dose to server: ${err.message}` });
      await refreshMeds({ silent: true });
    }
  };

  const removeMed = async (id) => {
    if (removingId) return;
    setRemovingId(id);
    try {
      await clinicMedsApi(patientId, `/${encodeURIComponent(id)}/`, "DELETE", { patient_id: patientId });
      await refreshMeds({ silent: true });
      setFlash({ type: "success", text: "Medication moved to history." });
    } catch (err) {
      setFlash({ type: "error", text: `Couldn't remove medication: ${err.message}` });
      await refreshMeds({ silent: true });
    } finally {
      setRemovingId(null);
    }
  };

  const addMed = async (e) => {
    e.preventDefault();
    const name = draft.name.trim();
    if (!name || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await clinicMedsApi(patientId, "/", "POST", {
        patient_id: patientId,
        name,
        dose: draft.dose.trim(),
        frequency: draft.slot,
        reason: draft.reason.trim(),
      });
      await refreshMeds({ silent: true });
      setDraft({ name: "", dose: "", reason: "", slot: "morning" });
      setShowForm(false);
      setFlash({ type: "success", text: `Added ${name} to the patient's medication list.` });
    } catch (err) {
      setSubmitError(err.message || "Failed to add medication");
    } finally {
      setSubmitting(false);
    }
  };

  const handleScanLatestDocument = async () => {
    if (docScanning) return;
    const token = await getAccessTokenSafe();
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    setDocScanning(true);
    setScanInFlight(true);
    setFlash({ type: "info", text: "Reading the patient's visits…" });
    // Mark this auto-scan run as in-flight so the header-button path
    // (and any subsequent programmatic re-opens of the modal today) won't
    // re-fire the scan until tomorrow. The "stamp successful completion"
    // call writes the actual day key once we know the backend gave us a
    // real response (success OR a clean no-meds find OR no-document). We
    // don't stamp on HTTP errors / exceptions — those should retry next
    // open. Stored in localStorage (via medsCache.js) so a hard refresh
    // or a new tab inside the same day still skips the auto-scan.
    const stampCompletion = () => {
      writeAutoScanStampToday({
        day: localDateKey(),
        source: autoScan ? "auto" : "manual",
      });
    };
    try {
      // Build the request body. `current_visit_override` carries the live
      // Quill delta for the visit the doctor is currently editing — the
      // backend uses it as the highest-priority source for that visit so
      // meds typed in the last few seconds (before autosave/yjs-flush
      // debounces fire) still show up in the scan result. We ship both
      // path + schema because the backend builds the storage path from
      // them (and they mirror what `usePatientDocument.buildDocPath`
      // produces on the chart side).
      let liveDelta = null;
      if (currentVisitOverride) {
        const quill = currentVisitOverride.getQuill
          ? currentVisitOverride.getQuill()
          : currentVisitOverride.quill;
        if (quill && typeof quill.getContents === "function") {
          try {
            const contents = quill.getContents();
            const ops = contents && Array.isArray(contents.ops) ? contents.ops : [];
            if (ops.length > 0) {
              liveDelta = { delta: { ops } };
            }
          } catch (_) {
            // Live override is best-effort — fall back to the storage
            // chain if Quill isn't mounted (e.g. modal opened from a
            // context that has no editor attached).
            liveDelta = null;
          }
        }
      }
      const body = { patient_id: patientId };
      if (
        liveDelta
        && currentVisitOverride
        && currentVisitOverride.visitId
        && currentVisitOverride.path
      ) {
        body.current_visit_override = {
          visit_id: currentVisitOverride.visitId,
          path: currentVisitOverride.path,
          schema: currentVisitOverride.schema || "",
          delta: liveDelta.delta,
        };
      }
      const res = await fetch(`${backendBase()}/api/clinic/patient-meds/scan-latest-document/`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFlash({ type: "error", text: payload?.error || payload?.detail || `Scan failed (${res.status}).` });
        return;
      }
      const status = payload.status;
      if (status === "unavailable" || status === "no_document") {
        setFlash({ type: "info", text: payload.error || "No visits with summaries were found yet." });
        stampCompletion();
        return;
      }
      const found = Array.isArray(payload.medications) ? payload.medications : [];
      if (found.length === 0) {
        const scanned = payload.visits_scanned;
        const label = scanned
          ? `Scanned ${scanned} visit${scanned === 1 ? "" : "s"} but no medicines were found.`
          : "No new medicines were found in the patient's visits.";
        setFlash({ type: "info", text: label });
        // Backend answered with a real, definitive "nothing to add" — count
        // that as today's scan done. We only skip the stamp on HTTP errors
        // / thrown exceptions, so transient 5xx / network blips retry next
        // open.
        stampCompletion();
        return;
      }
      await refreshMeds({ silent: true });
      const scanned = payload.visits_scanned;
      const scannedLabel = scanned
        ? ` from ${scanned} visit${scanned === 1 ? "" : "s"}`
        : "";
      // `payload.skipped` is the count of meds the backend already had on
      // the list (deduped by normalized name, see `_normalize_med_key`).
      // Hitting the button twice in a row on the same patient is the common
      // case, so show "M were already on your list" instead of silently
      // saying "Added 0" the second time around.
      const skipped = Number(payload.skipped) || 0;
      const skippedLabel = skipped > 0
        ? ` ${skipped} ${skipped === 1 ? "was" : "were"} already on the list.`
        : "";
      // Per-medication source-visit dates from the LLM (the visit block
      // headers in the prompt are `=== Visit N of M — YYYY-MM-DD with … ===`,
      // and the response carries them back as `source_visit_dates`). When
      // the model returned them we surface a per-med breakdown so the doctor
      // can see WHICH visits each med came from — not just the aggregate
      // count. When the model returned no dates we fall back to the old
      // "Added N medicines from N visits." copy. `payload.medications` is
      // already the deduped "actually-added" list from the backend, so
      // every med here is genuinely new — safe to attribute source dates
      // to its MedRow without polluting already-on-list rows.
      const perMedDates = found
        .map((m) => ({
          name: m && m.name,
          dates: Array.isArray(m && m.source_visit_dates) ? m.source_visit_dates : [],
        }))
        .filter((entry) => entry.name && entry.dates.length > 0);
      const perMedLabel = perMedDates.length > 0
        ? " " + perMedDates.map((entry) => {
            const pretty = entry.dates.map(prettyVisitDate).join(", ");
            return `${entry.name} (${pretty})`;
          }).join("; ")
        : "";
      // Persist the dates into state so each MedRow can render its
      // "From <dates>" line. Keyed by the same normalized name we use
      // to spot duplicate meds across scans, so a fresh scan overwrites
      // stale entries for the same drug and unrelated entries survive.
      if (perMedDates.length > 0) {
        setMedSourceDates((cur) => {
          const next = { ...cur };
          for (const entry of perMedDates) {
            next[normalizeMedName(entry.name)] = entry.dates;
          }
          return next;
        });
      }
      setFlash({
        type: "success",
        text: `Added ${found.length} medicine${found.length === 1 ? "" : "s"}${scannedLabel}.${skippedLabel}${perMedLabel}`,
      });
      stampCompletion();
    } catch (err) {
      setFlash({ type: "error", text: `Could not read the visits: ${err?.message || "unknown error"}.` });
    } finally {
      setDocScanning(false);
      setScanInFlight(false);
    }
  };

  const morning = meds.filter((m) => m.slot === "morning");
  const evening = meds.filter((m) => m.slot === "evening");
  const takenCount = meds.filter((m) => m.taken).length;

  const MedRow = ({ m, removable, showCheck, sourceVisitDates, isRemoving = false }) => {
    const sourceLabel = Array.isArray(sourceVisitDates) && sourceVisitDates.length > 0
      ? `From ${sourceVisitDates.map(prettyVisitDate).join(", ")}`
      : null;
    return (
      <div className={`pp-med-row ${m.taken ? "is-taken" : ""} ${isRemoving ? "is-removing" : ""}`}>
        {showCheck && (
          <button
            type="button"
            className={`pp-med-check ${m.taken ? "is-on" : ""}`}
            onClick={() => toggleTaken(m.id)}
            aria-label={m.taken ? "Mark as not taken" : "Mark as taken"}
            title={m.taken ? "Mark as not taken" : "Mark as taken"}
            disabled={isRemoving}
          >
            {m.taken ? "✓" : ""}
          </button>
        )}
        <div className="pp-med-body">
          <div className="pp-med-name">{m.name}</div>
          <div className="pp-med-dose">{m.dose} · {m.reason}</div>
          {m.taken && m.takenAt && <div className="pp-med-taken">Taken at {m.takenAt}</div>}
          {sourceLabel && (
            <div
              className="pp-med-source"
              title={`Extracted from visit(s): ${sourceVisitDates.join(", ")}`}
            >
              {sourceLabel}
            </div>
          )}
          {isRemoving && (
            <div className="pp-med-status" role="status" aria-live="polite">
              Removing…
            </div>
          )}
        </div>
        {removable && (
          <button
            type="button"
            className={`pp-med-remove ${isRemoving ? "is-loading" : ""}`}
            onClick={() => removeMed(m.id)}
            disabled={Boolean(removingId)}
            aria-label={isRemoving ? `Removing ${m.name}` : `Remove ${m.name}`}
            aria-busy={isRemoving}
            title={isRemoving ? "Removing…" : "Discontinue medication"}
          >
            {isRemoving ? (
              <span
                className="pp-spinner"
                aria-hidden="true"
              />
            ) : (
              "✕"
            )}
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="clinic-meds-modal-title"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-[#0a0a0f] text-slate-900 dark:text-slate-100 rounded-xl shadow-xl w-[760px] max-w-[95vw] max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 id="clinic-meds-modal-title" className="text-base font-semibold flex items-center gap-2">
              💊 Patient medications
            </h2>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {patient?.patient_name ? `For ${patient.patient_name} · ` : ""}
              {takenCount} of {meds.length} taken today · synced with the patient's /meds page
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {flash && (
          <div
            role="status"
            className={
              flash.type === "error"
                ? "mx-5 mt-3 rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-500/[0.08] px-3 py-2 text-sm font-semibold text-rose-800 dark:text-rose-200"
                : flash.type === "success"
                  ? "mx-5 mt-3 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/[0.08] px-3 py-2 text-sm font-semibold text-emerald-800 dark:text-emerald-200"
                  : "mx-5 mt-3 rounded-md border border-sky-300 bg-sky-50 dark:bg-sky-500/[0.08] px-3 py-2 text-sm font-semibold text-sky-800 dark:text-sky-200"
            }
          >
            {flash.text}
            <button
              type="button"
              onClick={() => setFlash(null)}
              className="ml-2 text-xs underline"
            >
              dismiss
            </button>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-auto px-5 py-4">
          {loading && !scanInFlight && <div className="text-sm text-slate-500">Loading the patient's medicines…</div>}
          {loadError && (
            <div className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-500/[0.08] px-3 py-2 text-sm font-semibold text-rose-800 dark:text-rose-200">
              {loadError}
            </div>
          )}

          {/* Auto-scan from the chart header: show a full spinner while the
              latest visits scan is in flight so the doctor doesn't see the
              previous (stale) meds list, then swap to the real list once
              refreshMeds completes inside handleScanLatestDocument. The
              scan can take 4–15 s — a centered spinner with copy is much
              less jarring than a table that briefly lies about the meds. */}
          {scanInFlight && autoScan && (
            <div
              className="flex flex-col items-center justify-center gap-3 py-12 text-slate-500 dark:text-slate-400"
              role="status"
              aria-live="polite"
            >
              <div className="h-10 w-10 rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-sky-500 animate-spin" />
              <div className="text-sm font-semibold">
                Reading {patient?.patient_name ? `${patient.patient_name}'s` : "the patient's"} visits…
              </div>
              <div className="text-xs">
                Pulling every clinic visit, then extracting active medicines.
              </div>
            </div>
          )}

          {!loading && !scanInFlight && (
            <>
              <div className="pp-meds-slot mb-4">
                <div className="pp-meds-slot-h text-sm font-semibold mb-2">This morning</div>
                {morning.length === 0 ? (
                  <div className="text-sm text-slate-500">No morning medicines.</div>
                ) : (
                  morning.map((m) => (
                    <MedRow
                      key={m.id}
                      m={m}
                      removable
                      showCheck
                      sourceVisitDates={medSourceDates[normalizeMedName(m.name)]}
                      isRemoving={removingId === m.id}
                    />
                  ))
                )}
              </div>
              <div className="pp-meds-slot mb-4">
                <div className="pp-meds-slot-h text-sm font-semibold mb-2">Tonight</div>
                {evening.length === 0 ? (
                  <div className="text-sm text-slate-500">No evening medicines.</div>
                ) : (
                  evening.map((m) => (
                    <MedRow
                      key={m.id}
                      m={m}
                      removable
                      showCheck
                      sourceVisitDates={medSourceDates[normalizeMedName(m.name)]}
                      isRemoving={removingId === m.id}
                    />
                  ))
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <button
                  type="button"
                  className="text-xs sm:text-sm font-medium px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200"
                  onClick={() => setShowForm((v) => !v)}
                >
                  ➕ {showForm ? "Cancel" : "Add a medication"}
                </button>
                <button
                  type="button"
                  className="text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-sm shadow-rose-500/25 disabled:opacity-70 inline-flex items-center gap-1.5"
                  onClick={handleScanLatestDocument}
                  disabled={docScanning}
                  title="Read every visit summary we have on file for this patient and propose new medications"
                >
                  {docScanning ? (
                    <LoadingClock label="Reading all visits…" />
                  ) : (
                    <>📄 Add meds from visits</>
                  )}
                </button>
              </div>

              {showForm && (
                <form onSubmit={addMed} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                  <label className="text-xs font-semibold flex flex-col gap-1">
                    Name
                    <input
                      type="text"
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      placeholder="e.g. Lisinopril 20 mg"
                      required
                      className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="text-xs font-semibold flex flex-col gap-1">
                    Dose
                    <input
                      type="text"
                      value={draft.dose}
                      onChange={(e) => setDraft({ ...draft, dose: e.target.value })}
                      placeholder="e.g. 1 tablet"
                      className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="text-xs font-semibold flex flex-col gap-1">
                    Reason
                    <input
                      type="text"
                      value={draft.reason}
                      onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
                      placeholder="e.g. for blood pressure"
                      className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <fieldset className="text-xs font-semibold flex flex-col gap-1">
                    <legend className="mb-1">When</legend>
                    <div className="flex gap-3">
                      <label className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          name="slot"
                          checked={draft.slot === "morning"}
                          onChange={() => setDraft({ ...draft, slot: "morning" })}
                        />
                        Morning
                      </label>
                      <label className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          name="slot"
                          checked={draft.slot === "evening"}
                          onChange={() => setDraft({ ...draft, slot: "evening" })}
                        />
                        Evening
                      </label>
                    </div>
                  </fieldset>
                  {submitError && (
                    <div className="sm:col-span-2 rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-500/[0.08] px-3 py-2 text-sm text-rose-800 dark:text-rose-200">
                      {submitError}
                    </div>
                  )}
                  <div className="sm:col-span-2 flex gap-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="text-sm font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white disabled:opacity-70"
                    >
                      {submitting ? "Saving…" : "Save medication"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      disabled={submitting}
                      className="text-sm font-medium px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              <div className="mt-6">
                <MedsHistory meds={meds} history={history} />
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
